import {
  DigitiseFormsDetails,
  GetFormsResponse,
} from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { Rule } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import {
  AutoRulesService,
  ListRulesResponse,
} from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/service.pb';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { fetchStripeReaders } from 'components/payments/queries/payments-terminal.query';
import { authorizedFetch } from 'helpers/utils';
import { selectPaymentUrl } from 'redux/actions/merchant/merchant.action';
import { DeviceModel } from '../../../../../../../apis/devices';
import { portingSchemaApi } from '../../../../../../../apis/porting/porting.api';
import { useLocationRegistrationStatusDetailsQuery } from '../../../../../../../apis/tendlc/queries';
import { voicemailBoxApi } from '../../../../../../../apis/voice-mailboxes/voice-mailboxes.api';
import { AppointmentWritebackModel } from '../../../../../../../models/appointment.model';
import { MerchantModel } from '../../../../../../../models/merchant.model';
import { SmsModel } from '../../../../../../../models/sms-history.model';
import { StatusMapping } from '../../../../../../../models/writebacks.model';
import { autoMsgDeliveryReportsApi } from '../../../../../../../redux/actions/auto-msg-queue/auto-msg-delivery-reports.action';
import { requestFaxHistory } from '../../../../../../../redux/actions/fax-history/api';
import { selectFeatureFlag } from '../../../../../../../redux/actions/feature-flags';
import {
  selectCurrentLocation,
  selectCurrentLocationParentId,
  selectCurrentLocationV2,
} from '../../../../../../../redux/actions/location';
import {
  MESSAGES_URL,
  OLD_MESSAGES_URL,
} from '../../../../../../../redux/actions/sms/api';
import { usersApi } from '../../../../../../../redux/actions/users/users.api';
import { CustomAxios, getResponseData } from '../../../../../../../redux/axios';
import CallRecordModel from '../../../../../../history-list-container/call-records-container/call-record.model';
import { Settings as ReviewsSettings } from '../../../../../../settings/reviews/helpers';
import { LocationHealth } from '../../../../../../sync-app/sync-app.types';
import { onboardingTasksApi } from '../../../../../onboarding.api';
import { DependencyDataKeys } from './handoff-metrics.types';
import { OnlineSchedulingHealthType } from './online-scheduling.typses';
import { PhoneTenantQueries } from 'apis/phone-tenant/queries';
import { CallRouteQueries } from 'apis/call-route/queries';
import { useWiePayerConnectionStatusQuery } from 'apis/insurance/queries';

export const useDependentMetricData = () => {
  const currentLocation = useSelector(selectCurrentLocation);
  const locationId = currentLocation?.LocationID;
  const parentLocationId = useSelector(selectCurrentLocationParentId);
  const locationV2 = useSelector(selectCurrentLocationV2);
  const commxMigrationFlag = useSelector((state) =>
    selectFeatureFlag(state, 'commx-migration')
  );
  const paymentsUrl = useSelector(selectPaymentUrl);
  const orgId = locationV2?.orgId;

  const queryConfig = {
    // cacheTime: hourCacheTime,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: false,
    onError: (err: AxiosError) => {
      console.error(err);
    },
  };

  const { data: phoneTenantId } = PhoneTenantQueries.useGetPhoneTenants(
    {
      orgId: orgId || '',
    },
    {
      select: (data) => data.tenants?.[0]?.id ?? '',
    }
  );

  const callCountsQuery = useQuery(
    [locationId, DependencyDataKeys.CallLogs],
    async () => {
      // PT-TODO: create an api file for these endpoints
      const resp = await CustomAxios.get<{ data: CallRecordModel[] }>(
        '/support/v1/calllogs',
        {
          params: {
            // PT-TODO: replace moment lib with whichever one we are using these days
            start: moment().subtract(1, 'week').startOf('day').toISOString(),
            end: moment().endOf('day').toISOString(),
          },
        }
      );

      const calls = resp.data.data || [];
      return calls;
    },
    queryConfig
  );

  const phoneDevicesQuery = useQuery(
    [locationId, DependencyDataKeys.PhoneDevices],
    async () => {
      // PT-TODO: create an api file for these endpoints
      const response = await CustomAxios.get<{ data: DeviceModel[] }>(
        '/support/v1/phone/devices'
      );
      const devices = response.data.data || [];

      return devices;
    },
    queryConfig
  );

  const smsQuery = useQuery(
    [locationId, DependencyDataKeys.Sms],
    async () => {
      const url = commxMigrationFlag ? MESSAGES_URL : OLD_MESSAGES_URL;
      const params = {
        params: {
          limit: 200,
          start: dayjs().subtract(7, 'day').toISOString(),
          end: new Date().toISOString(),
          location_id: locationId,
        },
      };

      const responses = await CustomAxios.get<{ smsList: SmsModel[] }>(url, params);

      const textMessages = responses.data?.smsList || [];
      return textMessages;
    },
    queryConfig
  );

  const usersQuery = useQuery(
    [locationId, DependencyDataKeys.Users],
    async () => {
      const users = (await usersApi.getUsers(200)) || [];
      return users;
    },
    queryConfig
  );

  const messagingRulesQuery = useQuery<ListRulesResponse, Error, Rule[] | undefined>(
    [locationId, DependencyDataKeys.MessagingRules, orgId],
    () =>
      AutoRulesService.ListRules(authorizedFetch(), {
        orgId,
        businessGroups: [locationId],
        skipFilterValidation: true,
        summaryOnly: true,
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!locationId && !!orgId,
      select: (data) => data.rules,
    }
  );

  const autoMsgLocationSettingsQuery = useQuery(
    [locationId, DependencyDataKeys.AutoMsgLocationSettings],
    async () => {
      const locationSettings = await autoMsgDeliveryReportsApi.getLocationSettings(
        locationId
      );

      return locationSettings;
    },
    queryConfig
  );

  const appointmentWritebacksQuery = useQuery(
    [locationId, DependencyDataKeys.AppointmentWritebacks],
    async () => {
      const resp = await CustomAxios.get<{ data: AppointmentWritebackModel[] }>(
        `/support/v1/appointments`,
        {
          params: {
            start: moment().subtract(1, 'week').format('YYYY-MM-DD'),
            end: moment().add(1, 'day').format('YYYY-MM-DD'),
          },
        }
      );

      const apptWritebacks = resp.data?.data || [];

      return apptWritebacks;
    },
    queryConfig
  );

  const writebacksStatusMappingsQuery = useQuery(
    [locationId, DependencyDataKeys.WritebacksStatusMappings],
    async () => {
      const resp = await CustomAxios.get<{ data: StatusMapping[] }>(
        `support/v1/notifications/status-mappings/writeback`,
        {
          // Multi singles - locations that have opted out of sharing data
          // (meaning they dont have a parent id sharing data)
          // Unify - locations that share data
          // Data Beta - locations that share data
          // We care to determine which locations share data down to the children for unify and data bata.
          // We can determine this from a ParentID

          headers: {
            'Location-Id': parentLocationId ? parentLocationId : locationId,
          },
        }
      );

      const statusMappingsSettings = resp.data?.data || [];

      return statusMappingsSettings;
    },
    queryConfig
  );

  const appointmentsStatusMappingsQuery = useQuery(
    [locationId, DependencyDataKeys.AppointmentsStatusMappings],
    async () => {
      const resp = await CustomAxios.get<{ data: StatusMapping[] }>(
        `support/v1/notifications/status-mappings/appointment`
      );

      const statusMappingsSettings = resp.data?.data || [];

      return statusMappingsSettings;
    },
    queryConfig
  );

  const syncappHealthQuery = useQuery(
    [locationId, DependencyDataKeys.SyncappHealth],
    async () => {
      const resp = await CustomAxios.get<{ data: LocationHealth }>(
        `support/v1/syncapp/health`,
        {
          // Multi singles - locations that have opted out of sharing data
          // (meaning they dont have a parent id sharing data)
          // Unify - locations that share data
          // Data Beta - locations that share data
          // We care to determine which locations share data down to the children for unify and data bata.
          // We can determine this from a ParentID
          headers: { 'Location-Id': parentLocationId ? parentLocationId : locationId },
        }
      );
      const data = resp?.data?.data ?? {};
      return data;
    },
    {
      // cacheTime: hourCacheTime,
      retry: false,
      refetchOnWindowFocus: false,
      onError: (err: AxiosError) => {
        console.error(err);
      },
    }
  );

  const reviewsSettingsQuery = useQuery(
    [locationId, DependencyDataKeys.ReviewsSettings],
    async () => {
      const resp = await CustomAxios.get<{ data: ReviewsSettings }>(
        'support/v1/reviews/settings'
      );
      const data = resp?.data?.data ?? {};

      return data;
    },
    queryConfig
  );

  const portingDataQuery = useQuery({
    queryKey: [locationId, DependencyDataKeys.PortingData],
    queryFn: () => portingSchemaApi.getPortingDataByLocationIds([locationId]),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!locationId,
    onError: console.error,
  });

  const faxHistoryQuery = useQuery(
    [locationId, DependencyDataKeys.FaxHistory],
    async () => {
      const params = {
        start: dayjs().subtract(7, 'day').toISOString(),
        end: new Date().toISOString(),
        fromdid: '',
        todid: '',
        limit: '26',
        skip: '',
        order: 'asc',
      };
      const data = await requestFaxHistory(params);

      return data ?? [];
    },
    queryConfig
  );

  const fetchFaxNumberDID = async () => {
    let response = await CustomAxios.get(`/support/v1/fax/did`).then(getResponseData);
    return response;
  };

  const faxProviderQuery = useQuery(
    [locationId, DependencyDataKeys.FaxProviderSettings],
    async () => {
      const faxNumber = await fetchFaxNumberDID();

      if (faxNumber.DID !== '') {
        const response = await CustomAxios.get(
          `support/v1/fax/providerdidinfo/${faxNumber['DID']}`
        );
        return response;
      }
      return;
    },
    queryConfig
  );

  const submittedDigitizationFormsQuery = useQuery(
    [locationId, DependencyDataKeys.SubmittedDigitizationForms],
    async () => {
      const resp = await CustomAxios.get<DigitiseFormsDetails>(
        `/forms/v1/digitization?companyId=${locationId}`
      );
      return resp?.data.data ?? [];
    },
    queryConfig
  );

  const digitalFormsQuery = useQuery(
    [locationId, DependencyDataKeys.DigitalForms],
    async () => {
      const response = await CustomAxios.get<GetFormsResponse>(
        `/forms-digital/v2/forms?company_ids=${locationId}&pageRequest.pageLimit=100&pageRequest.pageNumber=1&searchKey=`
      );
      const forms = response?.data?.data ?? [];
      return forms;
    },
    queryConfig
  );

  const onlineScheduleQuery = useQuery(
    [locationId, DependencyDataKeys.OnlineSchedulingHealth],
    async () => {
      const response = await CustomAxios.get<{ data: OnlineSchedulingHealthType }>(
        `schedule/api/v2/health`
      );
      const onlineSchedule = response?.data?.data;
      return onlineSchedule;
    },
    queryConfig
  );

  const customizationFeaturesQuery = useQuery(
    [locationId, DependencyDataKeys.CustomizationFlags],
    async () => {
      const resp = await CustomAxios.get(
        `platform-location-feature/v1/location/${locationId}/features`
      );
      return resp;
    },
    queryConfig
  );

  const useTaskQuery = useQuery(
    [locationId, DependencyDataKeys.LocationTasks],
    async () => {
      const resp = await onboardingTasksApi.getModulesTasks();
      return resp;
    },
    queryConfig
  );

  const usePaymentsQuery = useQuery(
    [locationId, paymentsUrl, DependencyDataKeys.PaymentsSettings],
    async () => {
      const resp = await CustomAxios.get<{ data: MerchantModel }>(`payments/v1/merchant`);
      const stripeReaders = await fetchStripeReaders({
        paymentsUrl,
        stripeLocationId: resp.data?.data?.terminalLocation,
      });
      return {
        merchantData: resp.data?.data,
        stripeReaders,
      };
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: false,
      onError: (err: AxiosError) => {
        console.error(err);
      },
    }
  );

  const { data: salesforceBundleQuery } = useQuery(
    [locationId, DependencyDataKeys.SalesforceBundleSettings],
    async () => {
      const resp = await CustomAxios.get(`/package-service/v1/salesforce-bundle-name`);
      return resp?.data;
    },
    {
      enabled: true,
      onError: (err: AxiosError) => {
        console.log(err);
      },
    }
  );

  const { query: tenDlcLocationRegistrationStatusQuery } =
    useLocationRegistrationStatusDetailsQuery(
      { locationId },
      {
        enabled: !!locationId,
        retry: false,
      }
    );

  const { query: wiePayerConnectionStatusQuery } = useWiePayerConnectionStatusQuery(
    { locationId },
    {
      enabled: !!locationId,
      retry: false,
    }
  );

  const phoneHoursQuery = CallRouteQueries.useGetPhoneHours(
    {
      tenantId: phoneTenantId || '',
    },
    {
      enabled: !!phoneTenantId,
    }
  );

  const { data: callRoutes = [] } = CallRouteQueries.useGetCallRouteList(
    {
      tenantId: phoneTenantId || '',
    },
    {
      enabled: !!phoneTenantId,
      select: (data) => data.callRoutes || [],
    }
  );

  const callRouteDetailListQuery = CallRouteQueries.useGetMultiCallRouteDetail(
    callRoutes.map((callRoute) => ({
      callRouteId: callRoute.callRouteId,
    })),
    {
      enabled: callRoutes.length > 0,
    }
  );

  return {
    [DependencyDataKeys.CallLogs]: callCountsQuery,
    [DependencyDataKeys.CallRoutesList]: callRouteDetailListQuery,
    [DependencyDataKeys.PhoneHours]: phoneHoursQuery,
    [DependencyDataKeys.PhoneDevices]: phoneDevicesQuery,
    [DependencyDataKeys.Sms]: smsQuery,
    [DependencyDataKeys.Users]: usersQuery,
    [DependencyDataKeys.MessagingRules]: messagingRulesQuery,
    [DependencyDataKeys.AutoMsgLocationSettings]: autoMsgLocationSettingsQuery,
    [DependencyDataKeys.AppointmentWritebacks]: appointmentWritebacksQuery,
    [DependencyDataKeys.WritebacksStatusMappings]: writebacksStatusMappingsQuery,
    [DependencyDataKeys.AppointmentsStatusMappings]: appointmentsStatusMappingsQuery,
    [DependencyDataKeys.SyncappHealth]: syncappHealthQuery,
    [DependencyDataKeys.PortingData]: portingDataQuery,
    [DependencyDataKeys.ReviewsSettings]: reviewsSettingsQuery,
    [DependencyDataKeys.FaxHistory]: faxHistoryQuery,
    [DependencyDataKeys.FaxProviderSettings]: faxProviderQuery,
    [DependencyDataKeys.DigitalForms]: digitalFormsQuery,
    [DependencyDataKeys.SubmittedDigitizationForms]: submittedDigitizationFormsQuery,
    [DependencyDataKeys.OnlineSchedulingHealth]: onlineScheduleQuery,
    [DependencyDataKeys.LocationTasks]: useTaskQuery,
    [DependencyDataKeys.CustomizationFlags]: customizationFeaturesQuery,
    [DependencyDataKeys.SalesforceBundleSettings]: salesforceBundleQuery,
    [DependencyDataKeys.PaymentsSettings]: usePaymentsQuery,
    [DependencyDataKeys.tenDlcLocationRegistrationStatus]:
      tenDlcLocationRegistrationStatusQuery,
    [DependencyDataKeys.WIEPayerConnectionStatus]: wiePayerConnectionStatusQuery,
  };
};
